// @flow

import { option } from 'fp-ts';
import type { BundleProductType } from '../../types/product';
import type {
  BundleConfiguration, BundleOption,
  BundleOptions, BundleOptionSelection,
  BundleOptionSelections, BundleOptionView,
} from '../../flowtypes/bundles';
import { getFirstKeyedObject, optionGet } from '../../helpers/objects';

export const hasOnlyVirtualSelections = (selections: BundleOptionSelections) => Object.keys(selections)
  // selections[current]: BundleOptionSelection
  .reduce((result: boolean, current: string) =>
    (result && selections[current].productType === 'virtual'), true);

export const hasOnlyVirtualOptions = (options: BundleOptions) => Object.keys(options)
  // options[current]: BundleOption
  .reduce((result: boolean, current: string) =>
    (result && hasOnlyVirtualSelections(options[current].selections)), true);

// If a bundle has any options that are not virtual products, the bundle is not virtual
export const isBundleVirtual = (product: BundleProductType) => option.fromNullable(product.bundle_options)
  .reduce((result: boolean, current: BundleConfiguration) =>
    (result && hasOnlyVirtualOptions(current.options)), true);

export const getFirstBundleSelection = (bundleOption: BundleOption) =>
  option.fromNullable(getFirstKeyedObject(bundleOption.selections));

export const getFirstBundleSelectionProp = (bundleOption: BundleOption) => (path: string) =>
  getFirstBundleSelection(bundleOption)
    .map((selection: BundleOptionSelection) => optionGet(path)(selection).getOrElseValue(null))
    .getOrElseValue('');

export const getBundleOptionView = (bundleOption: BundleOption): BundleOptionView => {
  const firstSelectionFn = getFirstBundleSelectionProp(bundleOption);
  return {
    name: firstSelectionFn('name'),
    price: parseInt(firstSelectionFn('prices.basePrice.amount'), 10),
    productId: firstSelectionFn('optionId'),
  };
};

export const getBundleOptions = (product: BundleProductType) => option.fromNullable(product.bundle_options)
  .map((bundleConfiguration: BundleConfiguration) =>
    Object.keys(bundleConfiguration.options)
      .map((key: string) => getBundleOptionView(bundleConfiguration.options[key])))
  .getOrElseValue([]);

export const getBundleSelection = (product: BundleProductType) => option.fromNullable(product.bundle_options)
  .map((bundleConfiguration: BundleConfiguration) => Object.keys(bundleConfiguration.selected)
    .map((key: string) => ({
      option_id: key,
      option_qty: 1,
      option_selections: bundleConfiguration.selected[key],
    })),
  )
  .getOrElseValue([]);



// WEBPACK FOOTER //
// ./src/store/modules/bundles.js